import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import Modal from "../../../components/modal/Modal";
import { Project, ProjectApprovalLevel } from "../../../types/api";
import { getAllPremises } from "../../../service/api";
import { useOrgUsersStore, useRoleManageStore } from "../../../store/store";
import { useProjectUtilityStore } from "../../../store/utilitySystemsStore";
import { useLocationStore } from "../../../store/masterDataStore";
import { Plus, Trash2 } from "lucide-react";

interface ProjectFormProps {
  isOpen: boolean;
  onClose: () => void;
  project?: Project | null;
  mode: "create" | "edit" | "view";
  onSuccess?: () => void;
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  isOpen,
  onClose,
  project,
  mode,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "Construction" as "Construction" | "Renovation",
    locationId: "",
    startDate: new Date(),
    endDate: new Date(),
    projectLeadId: "",
    estimatedCost: 0,
    description: "",
    status: "Draft" as "Draft" | "Pending Approval" | "Approved" | "Rejected" | "In-Progress" | "Completed",
    approvalLevels: [] as ProjectApprovalLevel[],
  });
  // const [premises, setPremises] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { allLocations, fetchAllLocations } = useLocationStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();
  const {
    projectsLoading,
    fetchProjectById,
    createProject,
    updateProject,
  } = useProjectUtilityStore();

  const isDisabled = mode === "view";
  const isReadOnly = mode === "view";

  const loadProjectData = useCallback(async () => {
    if (!project) return;

    try {
      const id = project.id || project._id;
      if (!id) return;

      const data = await fetchProjectById(id);
      if (data) {
        setFormData({
          name: data.name || "",
          type: data.type || "Construction",
          locationId: data.locationId || "",
          startDate: data.startDate ? new Date(data.startDate) : new Date(),
          endDate: data.endDate ? new Date(data.endDate) : new Date(),
          projectLeadId: data.projectLeadId || "",
          estimatedCost: data.estimatedCost || 0,
          description: data.description || "",
          status: data.status || "Draft",
          approvalLevels: data.approvalLevels || [],
        });
      } else {
        // Fallback
        setFormData({
          name: project.name || "",
          type: project.type || "Construction",
          locationId: project.locationId || "",
          startDate: project.startDate ? new Date(project.startDate) : new Date(),
          endDate: project.endDate ? new Date(project.endDate) : new Date(),
          projectLeadId: project.projectLeadId || "",
          estimatedCost: project.estimatedCost || 0,
          description: project.description || "",
          status: project.status || "Draft",
          approvalLevels: project.approvalLevels || [],
        });
      }
    } catch (error) {
      console.error("Error loading project:", error);
      toast.error("Failed to load project data");
      if (project) {
        setFormData({
          name: project.name || "",
          type: project.type || "Construction",
          locationId: project.locationId || "",
          startDate: project.startDate ? new Date(project.startDate) : new Date(),
          endDate: project.endDate ? new Date(project.endDate) : new Date(),
          projectLeadId: project.projectLeadId || "",
          estimatedCost: project.estimatedCost || 0,
          description: project.description || "",
          status: project.status || "Draft",
          approvalLevels: project.approvalLevels || [],
        });
      }
    }
  }, [project, fetchProjectById]);

  useEffect(() => {
    if (isOpen) {
      // fetchPremises();
      fetchAllLocations();
      fetchAllRoles();
      if (!orgUsersData.length) fetchOrgUsers();
      if (mode === "edit" || mode === "view") {
        loadProjectData();
      } else {
        setFormData({
          name: "",
          type: "Construction",
          locationId: "",
          startDate: new Date(),
          endDate: new Date(),
          projectLeadId: "",
          estimatedCost: 0,
          description: "",
          status: "Draft",
          approvalLevels: [],
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, fetchAllLocations, fetchAllRoles, fetchOrgUsers, orgUsersData.length, loadProjectData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Project Name is required";
    }

    if (!formData.locationId) {
      newErrors.locationId = "Location is required";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start Date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End Date is required";
    }

    if (formData.endDate <= formData.startDate) {
      newErrors.endDate = "End Date must be greater than Start Date";
    }

    if (!formData.projectLeadId) {
      newErrors.projectLeadId = "Project Lead is required";
    }

    if (formData.estimatedCost < 0) {
      newErrors.estimatedCost = "Estimated Cost must be greater than or equal to 0";
    }

    if (formData.status === "Pending Approval") {
      if (formData.approvalLevels.length === 0) {
        newErrors.approvalLevels = "At least one approval level is required when submitting for approval";
      } else {
        formData.approvalLevels.forEach((level, index) => {
          if (!level.roleId) {
            newErrors[`approvalLevel_${index}_role`] = "Role is required";
          }
          if (!level.userId) {
            newErrors[`approvalLevel_${index}_user`] = "User is required";
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const projectData: any = {
        ...formData,
        startDate: formData.startDate ? formData.startDate.toISOString() : null,
        endDate: formData.endDate ? formData.endDate.toISOString() : null,
      };

      if (formData.approvalLevels && formData.approvalLevels.length > 0) {
        projectData.approvalLevels = formData.approvalLevels.map((level) => ({
          roleId: level.roleId,
          roleName: level.roleName,
          userId: level.userId,
          userName: level.userName,
        }));
      } else {
        projectData.approvalLevels = [];
      }

      if (mode === "create") {
        await createProject(projectData as Partial<Project>);
        toast.success("Project created successfully");
      } else {
        const id = project?.id || project?._id;
        if (id) {
          await updateProject(id, projectData as Partial<Project>);
          toast.success("Project updated successfully");
        }
      }

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error("Error saving project:", error);
      toast.error(error.response?.data?.error || error.message || "Failed to save project");
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const addApprovalLevel = () => {
    if (formData.approvalLevels.length >= 5) {
      toast.error("Maximum 5 approval levels allowed");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      approvalLevels: [
        ...prev.approvalLevels,
        {
          level: prev.approvalLevels.length + 1,
          roleId: "",
          roleName: "",
          userId: "",
          userName: "",
          status: "Pending" as const,
        },
      ],
    }));
  };

  const removeApprovalLevel = (index: number) => {
    setFormData((prev) => {
      const newLevels = prev.approvalLevels.filter((_, i) => i !== index);
      return {
        ...prev,
        approvalLevels: newLevels.map((level, i) => ({ ...level, level: i + 1 })),
      };
    });
  };

  const updateApprovalLevel = (index: number, field: keyof ProjectApprovalLevel, value: any) => {
    setFormData((prev) => {
      const newLevels = [...prev.approvalLevels];
      const level = { ...newLevels[index] };

      if (field === "roleId") {
        const role = rolesData.find((r: any) => (r._id || r.id) === value);
        level.roleId = value;
        level.roleName = role ? (role.roleName || (role as any).userRole || "") : "";
        level.userId = "";
        level.userName = "";
      } else if (field === "userId") {
        const user = orgUsersData.find((u: any) => (u._id || u.id) === value);
        level.userId = value;
        level.userName = user
          ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || ""
          : "";
      } else {
        (level as any)[field] = value;
      }

      newLevels[index] = level;
      return { ...prev, approvalLevels: newLevels };
    });
  };

  const getUsersByRole = (roleId: string) => {
    if (!roleId) return [];
    return orgUsersData.filter((user: any) => {
      const userRoleId = typeof user.role === "string" ? user.role : user.role?._id || user.role?.id;
      return userRoleId === roleId;
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeButton={false}
      modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {mode === "create"
            ? "Create New Project"
            : mode === "edit"
            ? "Edit Project"
            : "View Project"}
        </h2>
        <form onSubmit={handleSubmit} id="projectForm" className="flex flex-col gap-6 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                disabled={isDisabled}
                className={`w-full p-2 border ${
                  errors.name ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                placeholder="Enter project name"
                required
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={(e) =>
                  handleChange("type", e.target.value as "Construction" | "Renovation")
                }
                disabled={isDisabled}
                className={`w-full p-2 border ${
                  errors.type ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
              >
                <option value="Construction">Construction</option>
                <option value="Renovation">Renovation</option>
              </select>
              {errors.type && (
                <p className="text-red-500 text-sm mt-1">{errors.type}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.locationId}
                onChange={(e) => handleChange("locationId", e.target.value)}
                disabled={isDisabled}
                className={`w-full p-2 border ${
                  errors.locationId ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
              >
                <option value="">Select Location</option>
                {allLocations.map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.room} - {location.floorNumber}
                  </option>
                ))}
                {/* {premises.map((premise) => (
                  <option key={premise._id || premise.id} value={premise._id || premise.id}>
                    {premise.premisesName || premise.name}
                  </option>
                ))} */}
              </select>
              {errors.locationId && (
                <p className="text-red-500 text-sm mt-1">{errors.locationId}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Project Lead <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.projectLeadId}
                onChange={(e) => handleChange("projectLeadId", e.target.value)}
                disabled={isDisabled}
                className={`w-full p-2 border ${
                  errors.projectLeadId ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                required
              >
                <option value="">Select Project Lead</option>
                {orgUsersData.map((user: any) => (
                  <option key={user._id || user.id} value={user._id || user.id}>
                    {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                      user.email ||
                      user._id ||
                      user.id}
                  </option>
                ))}
              </select>
              {errors.projectLeadId && (
                <p className="text-red-500 text-sm mt-1">{errors.projectLeadId}</p>
              )}
            </div>

            {/* Start Date */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Start Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={formData.startDate}
                onChange={(date: Date | null) =>
                  handleChange("startDate", date || new Date())
                }
                disabled={isDisabled}
                placeholderText="Select start date"
                dateFormat="dd/MM/yyyy"
                wrapperClassName="datePicker"
                className={`w-full p-2 border ${
                  errors.startDate ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                readOnly={isReadOnly}
              />
              {errors.startDate && (
                <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>
              )}
            </div>

            {/* End Date */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                End Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={formData.endDate}
                onChange={(date: Date | null) =>
                  handleChange("endDate", date || new Date())
                }
                disabled={isDisabled}
                minDate={formData.startDate ? formData.startDate : undefined}
                placeholderText="Select end date"
                dateFormat="dd/MM/yyyy"
                wrapperClassName="datePicker"
                className={`w-full p-2 border ${
                  errors.endDate ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                readOnly={isReadOnly}
              />
              {errors.endDate && (
                <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Estimated Cost <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.estimatedCost}
                onChange={(e) =>
                  handleChange("estimatedCost", parseFloat(e.target.value) || 0)
                }
                disabled={isDisabled}
                min="0"
                step="0.01"
                className={`w-full p-2 border ${
                  errors.estimatedCost ? "border-red-500" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                placeholder="0.00"
                required
              />
              {errors.estimatedCost && (
                <p className="text-red-500 text-sm mt-1">{errors.estimatedCost}</p>
              )}
            </div>

            {mode !== "create" && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    handleChange("status", e.target.value as Project["status"])
                  }
                  disabled={isDisabled || formData.status === "Completed"}
                  className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                    isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                >
                  <option value="Draft">Draft</option>
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="In-Progress">In-Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
            )}

            <div className={`flex flex-col gap-2 ${mode !== "create" ? "md:col-span-2" : ""}`}>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                disabled={isDisabled}
                rows={3}
                className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                placeholder="Enter project description (optional)"
                readOnly={isReadOnly}
              />
            </div>
          </div>

          {mode !== "view" && (
            <div className="border-t pt-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Approval Levels</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Add up to 5 approval levels. Each level requires a role and assigned user.
                  </p>
                </div>
                {formData.approvalLevels.length < 5 && (
                  <button
                    type="button"
                    onClick={addApprovalLevel}
                    className="flex items-center gap-2 px-4 py-2 bg-[#375998] text-white rounded-md hover:bg-[#2d4a7a] transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Level
                  </button>
                )}
              </div>

              {formData.approvalLevels.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">No approval levels added yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Click "Add Level" to create an approval workflow
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.approvalLevels.map((level, index) => (
                    <div
                      key={index}
                      className="border border-gray-300 rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-700">
                          Approval Level {level.level}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeApprovalLevel(index)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium text-gray-700">
                            Role <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={level.roleId}
                            onChange={(e) =>
                              updateApprovalLevel(index, "roleId", e.target.value)
                            }
                            disabled={isDisabled}
                            className={`w-full p-2 border ${
                              errors[`approvalLevel_${index}_role`]
                                ? "border-red-500"
                                : "border-gray-300"
                            } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                              isDisabled ? "bg-gray-100 cursor-not-allowed" : ""
                            }`}
                            required
                          >
                            <option value="">Select Role</option>
                            {rolesData.map((role: any) => (
                              <option
                                key={role._id || role.id}
                                value={role._id || role.id}
                              >
                                {role.roleName || role.userRole || role._id || role.id}
                              </option>
                            ))}
                          </select>
                          {errors[`approvalLevel_${index}_role`] && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors[`approvalLevel_${index}_role`]}
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium text-gray-700">
                            User <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={level.userId}
                            onChange={(e) =>
                              updateApprovalLevel(index, "userId", e.target.value)
                            }
                            disabled={isDisabled || !level.roleId}
                            className={`w-full p-2 border ${
                              errors[`approvalLevel_${index}_user`]
                                ? "border-red-500"
                                : "border-gray-300"
                            } rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                              isDisabled || !level.roleId
                                ? "bg-gray-100 cursor-not-allowed"
                                : ""
                            }`}
                            required
                          >
                            <option value="">
                              {!level.roleId
                                ? "Select Role First"
                                : "Select User"}
                            </option>
                            {getUsersByRole(level.roleId).map((user: any) => (
                              <option key={user._id || user.id} value={user._id || user.id}>
                                {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                                  user.email ||
                                  user._id ||
                                  user.id}
                              </option>
                            ))}
                          </select>
                          {errors[`approvalLevel_${index}_user`] && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors[`approvalLevel_${index}_user`]}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="h-20" />
        </form>
      </div>

        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-[#6b7d96] transition"
            onClick={onClose}
            disabled={projectsLoading}
            >
            Cancel
          </button>
            {!isReadOnly && (
          <button
            type="submit"
            form="projectForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={projectsLoading}
          >
            {projectsLoading ? "Saving..." : mode === "create" ? "Create" : "Update"}
          </button>
      )}
        </div>
    </Modal>
  );
};

export default ProjectForm;



